import React, { useContext } from "react";
import { ThemeContext } from "../ThemeContext";

// Статические импорты всех иконок
import accountDark from "../images/icons/account_dark.svg";
import accountWhite from "../images/icons/account_white.svg";
import deleteDark from "../images/icons/delete_dark.svg";
import deleteWhite from "../images/icons/delete_white.svg";
import editDark from "../images/icons/edit_dark.svg";
import editWhite from "../images/icons/edit_white.svg";
import xDark from "../images/icons/x_dark.svg";
import xWhite from "../images/icons/x_white.svg";
import sendBlack from "../images/icons/send_black.svg";
import sendWhite from "../images/icons/send_white.svg";
import draftDark from "../images/icons/draft_dark.svg";
import draftWhite from "../images/icons/draft_white.svg";
import lockDark from "../images/icons/lock_dark.svg";
import lockWhite from "../images/icons/lock_white.svg";
import mailDark from "../images/icons/mail_dark.svg";
import mailWhite from "../images/icons/mail_white.svg";
import reportsDark from "../images/icons/reports_dark.svg";
import reportsWhite from "../images/icons/reports_white.svg";
import add1Dark from "../images/icons/add_dark1.svg";
import add1White from "../images/icons/add_white1.svg";
import commentDark from "../images/icons/comment_dark.svg";
import commentWhite from "../images/icons/comment_white.svg";
import dashboardDark from "../images/icons/dashboard_dark.svg";
import dashboardWhite from "../images/icons/dashboard_white.svg";
import folderDark from "../images/icons/folder_dark.svg";
import folderWhite from "../images/icons/folder_white.svg";
import groupsDark from "../images/icons/groups_dark.svg";
import groupsWhite from "../images/icons/groups_white.svg";
import likeDark from "../images/icons/like_dark.svg";
import likeWhite from "../images/icons/like_white.svg";
import likeHeartDark from "../images/icons/like_heart_dark.svg";
import likeHeartWhite from "../images/icons/like_heart_white.svg";
import moreHorizontalDark from "../images/icons/more_horrizontal_dark.svg";
import moreHorizontalWhite from "../images/icons/more_horrizontal_white.svg";
import moreVerticalDark from "../images/icons/more_vertical_dark.svg";
import moreVerticalWhite from "../images/icons/more_vertical_white.svg";
import saveDark from "../images/icons/save_dark.svg";
import saveWhite from "../images/icons/save_white.svg";
import scheduleDark from "../images/icons/schedule_dark.svg";
import scheduleWhite from "../images/icons/schedule_white.svg";
import shareDark from "../images/icons/share_dark.svg";
import shareWhite from "../images/icons/share_white.svg";
import teamDark from "../images/icons/team_dark.svg";
import teamWhite from "../images/icons/team_white.svg";
import trashDark from "../images/icons/trash_dark.svg";
import trashWhite from "../images/icons/trash_white.svg";

// Маппинг иконок
const iconMap = {
  // Основные иконки
  account: { dark: accountDark, white: accountWhite },
  delete: { dark: deleteDark, white: deleteWhite },
  edit: { dark: editDark, white: editWhite },
  x: { dark: xDark, white: xWhite },
  send_black: { dark: sendBlack, white: sendWhite },
  draft: { dark: draftDark, white: draftWhite },
  lock: { dark: lockDark, white: lockWhite },
  mail: { dark: mailDark, white: mailWhite },
  reports: { dark: reportsDark, white: reportsWhite },

  // Дополнительные иконки
  add1: { dark: add1Dark, white: add1White },
  comment: { dark: commentDark, white: commentWhite },
  dashboard: { dark: dashboardDark, white: dashboardWhite },
  folder: { dark: folderDark, white: folderWhite },
  groups: { dark: groupsDark, white: groupsWhite },
  like: { dark: likeDark, white: likeWhite },
  like_heart: { dark: likeHeartDark, white: likeHeartWhite },
  more_horrizontal: { dark: moreHorizontalDark, white: moreHorizontalWhite },
  more_vertical: { dark: moreVerticalDark, white: moreVerticalWhite },
  save: { dark: saveDark, white: saveWhite },
  schedule: { dark: scheduleDark, white: scheduleWhite },
  share: { dark: shareDark, white: shareWhite },
  team: { dark: teamDark, white: teamWhite },
  trash: { dark: trashDark, white: trashWhite },
};

/**
 * Адаптивная иконка, которая автоматически меняется в зависимости от темы
 * @param {Object} props - Пропсы компонента
 * @param {string} props.iconName - Имя иконки без суффикса темы
 * @param {string} [props.alt] - Альтернативный текст
 * @param {string} [props.className] - CSS классы
 * @param {Object} [props.style] - Инлайн стили
 * @param {number} [props.width] - Ширина иконки
 * @param {number} [props.height] - Высота иконки
 * @param {Function} [props.onClick] - Обработчик клика
 * @returns {JSX.Element} React компонент иконки
 */
const AdaptiveIcon = ({
  iconName,
  alt = "",
  className = "",
  style = {},
  width = 24,
  height = 24,
  onClick,
  ...props
}) => {
  const { theme } = useContext(ThemeContext);

  // Получаем правильную иконку из маппинга
  const iconData = iconMap[iconName];

  if (!iconData) {
    console.warn(`Icon not found: ${iconName}`);
    return (
      <div
        style={{
          width,
          height,
          backgroundColor: "#666",
          borderRadius: "2px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "8px",
          color: "#fff",
          ...style,
        }}
        className={className}
        onClick={onClick}
        {...props}>
        ?
      </div>
    );
  }

  const iconSrc = theme === "light" ? iconData.dark : iconData.white;

  // Объединяем стили
  const finalStyle = {
    width,
    height,
    ...style,
  };

  return (
    <img
      src={iconSrc}
      alt={alt || iconName}
      className={className}
      style={finalStyle}
      onClick={onClick}
      {...props}
    />
  );
};

/**
 * HOC для создания адаптивных иконок с предустановленными размерами
 * @param {string} iconName - Имя иконки
 * @param {number} defaultWidth - Ширина по умолчанию
 * @param {number} defaultHeight - Высота по умолчанию
 * @returns {Function} React компонент
 */
export const createAdaptiveIcon = (
  iconName,
  defaultWidth = 24,
  defaultHeight = 24
) => {
  return (props) => (
    <AdaptiveIcon
      iconName={iconName}
      width={defaultWidth}
      height={defaultHeight}
      {...props}
    />
  );
};

/**
 * Предустановленные адаптивные иконки
 */
export const DeleteIcon = createAdaptiveIcon("delete", 25, 25);
export const EditIcon = createAdaptiveIcon("edit", 15, 15);
export const CancelIcon = createAdaptiveIcon("x", 15, 15);
export const SendIcon = createAdaptiveIcon("send_black", 15, 15);
export const DraftIcon = createAdaptiveIcon("draft", 15, 15);
export const LockIcon = createAdaptiveIcon("lock", 16, 16);
export const MailIcon = createAdaptiveIcon("mail", 16, 16);
export const ReportsIcon = createAdaptiveIcon("reports", 20, 20);
export const AccountIcon = createAdaptiveIcon("account", 24, 24);

// Дополнительные иконки
export const AddIcon = createAdaptiveIcon("add1", 20, 20);
export const CommentIcon = createAdaptiveIcon("comment", 16, 16);
export const DashboardIcon = createAdaptiveIcon("dashboard", 20, 20);
export const FolderIcon = createAdaptiveIcon("folder", 18, 18);
export const GroupsIcon = createAdaptiveIcon("groups", 20, 20);
export const LikeIcon = createAdaptiveIcon("like", 16, 16);
export const LikeHeartIcon = createAdaptiveIcon("like_heart", 16, 16);
export const MoreHorizontalIcon = createAdaptiveIcon(
  "more_horrizontal",
  16,
  16
);
export const MoreVerticalIcon = createAdaptiveIcon("more_vertical", 16, 16);
export const SaveIcon = createAdaptiveIcon("save", 16, 16);
export const ScheduleIcon = createAdaptiveIcon("schedule", 18, 18);
export const ShareIcon = createAdaptiveIcon("share", 16, 16);
export const TeamIcon = createAdaptiveIcon("team", 20, 20);
export const TrashIcon = createAdaptiveIcon("trash", 18, 18);

export default AdaptiveIcon;
